import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { toast } from "sonner";
import { CreditCard, Wallet, Store, ArrowLeft } from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

export default function StorePaymentPage() {
    const { storeId } = useParams();
    const navigate = useNavigate();
    const { user, isAuthenticated, token } = useAuth();

    const [store, setStore] = useState(null);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState("moncash");

    const ACTIVATION_FEE = 2500.00; // Hardcoded fee for now

    // Effect to fetch store details
    useEffect(() => {
        const fetchStore = async () => {
            try {
                const response = await axios.get(`${API_URL}/stores/${storeId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setStore(response.data);

                // If already paid AND not rejected, redirect. 
                // If rejected, allow payment (re-payment).
                if (response.data.is_paid && response.data.status !== 'REJECTED') {
                    toast.success("Boutique déjà payée !");
                    navigate("/mes-boutiques");
                }
            } catch (error) {
                console.error("Error fetching store:", error);
                toast.error("Boutique introuvable");
                navigate("/mes-boutiques");
            } finally {
                setLoading(false);
            }
        };

        if (isAuthenticated && token) {
            fetchStore();
        } else {
            navigate("/connexion");
        }
    }, [storeId, isAuthenticated, token, navigate]);

    const handlePayment = async () => {
        if (!store) return;
        setProcessing(true);
        try {
            if (paymentMethod === "moncash") {
                // Initiate MonCash payment → redirect to MonCash portal
                const res = await axios.post(
                    `${API_URL}/payments/moncash/initiate/${storeId}`,
                    {},
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                // Redirect the browser to the MonCash payment portal
                window.open(res.data.payment_url, "_blank", "noopener,noreferrer");
                return;
            }

            if (paymentMethod === "wallet") {
                const res = await axios.post(
                    `${API_URL}/stores/${storeId}/pay`,
                    { payment_method: "wallet", amount: ACTIVATION_FEE, currency: "HTG" },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                toast.success("Paiement réussi ! Votre boutique est en attente de validation.");
                navigate("/mes-boutiques");
                return;
            }

            // Carte bancaire : à implémenter
            toast.error("Ce mode de paiement n'est pas encore disponible.");
        } catch (error) {
            console.error("Payment error:", error);
            toast.error(error.response?.data?.detail || "Échec du paiement. Veuillez réessayer.");
        } finally {
            setProcessing(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <p>Chargement de la boutique...</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <Navbar />

            <div className="container mx-auto px-4 py-8 max-w-2xl">
                <Button variant="ghost" onClick={() => navigate("/mes-boutiques")} className="mb-6">
                    <ArrowLeft className="mr-2 h-4 w-4" /> Retour à mes boutiques
                </Button>

                <Card className="shadow-lg border-t-4 border-t-primary">
                    <CardHeader className="text-center pb-2">
                        <div className="mx-auto bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                            <Store className="h-8 w-8 text-primary" />
                        </div>
                        <CardTitle className="text-2xl">Activation de Boutique</CardTitle>
                        <CardDescription>
                            Finalisez l'ouverture de votre boutique <strong>{store?.name}</strong>
                        </CardDescription>
                    </CardHeader>

                    <CardContent className="space-y-8">
                        {/* Amount Display */}
                        <div className="bg-gray-100 p-6 rounded-lg text-center">
                            <p className="text-sm text-gray-500 uppercase tracking-wide font-semibold mb-1">Montant à payer</p>
                            <p className="text-4xl font-bold text-gray-900">{ACTIVATION_FEE.toFixed(2)} <span className="text-xl text-gray-500">HTG</span></p>
                            <p className="text-xs text-gray-500 mt-2">Paiement unique pour l'activation</p>
                        </div>

                        {/* Payment Methods */}
                        <div className="space-y-4">
                            <Label className="text-base font-semibold">Choisissez votre mode de paiement</Label>
                            <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="grid grid-cols-1 gap-3">

                                {/* MonCash */}
                                <div className={`flex items-center space-x-3 p-4 border rounded-lg cursor-pointer transition-all ${paymentMethod === 'moncash' ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'hover:bg-gray-50'}`}>
                                    <RadioGroupItem value="moncash" id="moncash" />
                                    <Label htmlFor="moncash" className="flex-1 cursor-pointer flex justify-between items-center">
                                        <span className="font-medium flex items-center gap-2">📱 MonCash</span>
                                    </Label>
                                </div>

                                {/* Credit Card */}
                                <div className={`flex items-center space-x-3 p-4 border rounded-lg cursor-pointer transition-all ${paymentMethod === 'card' ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'hover:bg-gray-50'}`}>
                                    <RadioGroupItem value="card" id="card" />
                                    <Label htmlFor="card" className="flex-1 cursor-pointer flex justify-between items-center">
                                        <span className="font-medium flex items-center gap-2"><CreditCard className="h-4 w-4" /> Carte Bancaire</span>
                                    </Label>
                                </div>

                                {/* Wallet (Optional) */}
                                <div className={`flex items-center space-x-3 p-4 border rounded-lg cursor-pointer transition-all ${paymentMethod === 'wallet' ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'hover:bg-gray-50'} ${user?.wallet_balance < ACTIVATION_FEE ? 'opacity-60 grayscale' : ''}`}>
                                    <RadioGroupItem value="wallet" id="wallet" disabled={user?.wallet_balance < ACTIVATION_FEE} />
                                    <Label htmlFor="wallet" className="flex-1 cursor-pointer">
                                        <div className="flex justify-between items-center">
                                            <span className="font-medium flex items-center gap-2"><Wallet className="h-4 w-4" /> Portefeuille BuyMore</span>
                                            <span className="text-sm text-gray-500">{user?.wallet_balance?.toFixed(2)} HTG</span>
                                        </div>
                                        {user?.wallet_balance < ACTIVATION_FEE && (
                                            <p className="text-xs text-red-500 mt-1">Solde insuffisant</p>
                                        )}
                                    </Label>
                                </div>

                            </RadioGroup>
                        </div>

                        {/* MonCash info */}
                        {paymentMethod === 'moncash' && (
                            <div className="animate-in fade-in slide-in-from-top-2 p-4 bg-orange-50 rounded-lg border border-orange-200">
                                <p className="text-sm text-orange-800 font-medium">📱 Vous allez être redirigé vers le portail MonCash pour finaliser votre paiement de <strong>{ACTIVATION_FEE.toFixed(2)} HTG</strong>.</p>
                            </div>
                        )}

                        {paymentMethod === 'card' && (
                            <div className="animate-in fade-in slide-in-from-top-2 space-y-3 p-4 bg-gray-50 rounded-lg border">
                                <div className="space-y-1">
                                    <Label>Numéro de carte</Label>
                                    <Input placeholder="0000 0000 0000 0000" />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-1">
                                        <Label>Expiration</Label>
                                        <Input placeholder="MM/YY" />
                                    </div>
                                    <div className="space-y-1">
                                        <Label>CVC</Label>
                                        <Input placeholder="123" />
                                    </div>
                                </div>
                            </div>
                        )}

                        <Button
                            className="w-full h-12 text-lg bg-green-600 hover:bg-green-700 font-semibold"
                            onClick={handlePayment}
                            disabled={processing}
                        >
                            {processing ? "Traitement..." : `Payer ${ACTIVATION_FEE.toFixed(2)} HTG`}
                        </Button>

                        <p className="text-center text-xs text-gray-500">
                            Paiement sécurisé et crypté. En payant, vous acceptez nos conditions générales de vente pour les vendeurs.
                        </p>

                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
